import { useTranslation } from "react-i18next";
import { ListItem } from "../ui/ListItem";
import { cn } from "../../lib/utils";
import Loader from "../Loader";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import {
  practiceProfilesInPickerOrder,
  practicesOnAxisSide,
  type OfferedProfile,
  type PracticePickerPhase,
  type PracticeProfileOffer,
} from "../../domains/practice/practiceProfileSelection";

/**
 * The Practice Profiles screen's body, shared by the onboarding slide, the
 * settings editor and the Discovery sheet so the three cannot drift into
 * different renderings of the same question (practice-profiles ticket 08).
 *
 * Presentational: it owns no state, issues no request, and — since the axis went
 * multi-select — navigates nowhere. A tap on an axis option COCHES it; leaving
 * the question is the host's own footer, which is the only place that knows
 * whether the answer blocks (signup) or not (settings). Every host holds the
 * phase, the search text and the selection, and passes them down — the
 * onboarding flow in its store, the editor in its form.
 */
export function PracticeProfilesPicker({
  offer,
  selected,
  checkedOnOpen,
  disambiguation,
  specialtyLabel,
  onChangeSpecialty,
  phase,
  surface,
  matching,
  isAssisting,
  onToggle,
  onToggleDisambiguation,
  onSetPractices,
  onPhaseChange,
  variant,
}: {
  offer: PracticeProfileOffer;
  selected: ReadonlySet<string>;
  /**
   * The checked set as the list was ENTERED, which is what the rows are ordered
   * by. Separate from `selected` on purpose: see
   * `practiceProfilesInPickerOrder` — ordering by the live selection would move
   * a row out from under the finger that just toggled it.
   */
  checkedOnOpen: ReadonlySet<string>;
  /** The sides of the axis currently answered — null until the practitioner (or
   * the evidence) says anything. Several, because a working life is: see
   * `practicesOnAxisSide`. */
  disambiguation: readonly string[] | null;
  /** The Specialty these practices belong to, for the recap row. */
  specialtyLabel: string | null;
  /**
   * The way BACK to the question that produced that Specialty — tapping it in
   * the recap row reopens it. Null where there is nothing to reopen: the
   * settings editor edits the Specialty on another screen entirely, and a
   * single-specialty profession was never asked. The label then renders as plain
   * text rather than as a button that goes nowhere.
   */
  onChangeSpecialty: (() => void) | null;
  phase: PracticePickerPhase;
  /**
   * Which screen this is, and therefore which ways out it offers and who prints
   * the axis question. One union rather than a flag per difference: they all
   * follow from the same fact, and three booleans is three places for a host to
   * describe a screen that does not exist.
   *
   * The signup carousel has a title band of its own and the design puts the
   * question in it (Figma node 6681:3600), so the picker must not print it a
   * second time there; the settings sheet and the Explore gate title themselves
   * ("My practice", "What do you practise?") and would otherwise show a list of
   * sides with the question missing.
   *
   * "Check all" is absent from signup, and that is the reason this prop exists:
   * a measurable share of new accounts arrived carrying their Specialty's entire
   * catalogue, because it was the shortest way past a screen that refuses an
   * empty answer. Settings keeps it — someone who opens that sheet months later
   * is re-stating an answer rather than escaping a question. "Uncheck all"
   * survives in both, because emptying the list leaves signup unsubmittable
   * (`canSubmitPracticeProfiles`) and so cannot be the shortcut.
   *
   * `declaration` is the way out for a practitioner the catalogue does not
   * cover: their own words, sent as they typed them. `null` is the control arm
   * of `PRACTICE_DECLARATION`, where the search is still the only exit — the
   * flag is read by the host, so this component never learns an arm exists.
   * `query` is what is in the search field; the row only renders once it holds
   * something.
   *
   * Settings carries two more, and they are the whole difference between the
   * surfaces: `held` are the words already saved, which only a screen you come
   * back to can show — signup's tap leaves — and `onClear` drops them, because
   * a correction that can only replace is not one.
   */
  surface:
    | {
        readonly kind: "signup";
        readonly declaration: {
          readonly query: string;
          readonly onDeclare: (words: string) => void;
        } | null;
      }
    | {
        readonly kind: "settings";
        readonly declaration: {
          readonly query: string;
          readonly held: string | null;
          readonly onDeclare: (words: string) => void;
          readonly onClear: () => void;
        } | null;
      };
  /**
   * What the search found, or null when nothing is typed and the whole
   * catalogue is listed.
   *
   * The FIELD is never here: every host puts it in its own bottom band, above
   * the CTA — the carousel with the country, profession and specialty ones, the
   * editor above Save — so the practice list is not the one whose field scrolls
   * away with the rows. The host that owns the input runs the search with
   * `usePracticeSearchAssist` and owns the catalogue-miss report with it, which
   * is right: a miss at signup costs the practitioner the screen, a miss in
   * settings costs them nothing.
   *
   * Already ordered: the string layer alphabetically, then what the model
   * recognized, appended. This component neither searches nor re-orders them. A
   * row already under a thumb must not move because a network answer landed two
   * seconds later, and an order rebuilt here would do exactly that.
   */
  matching: readonly OfferedProfile[] | null;
  /** That answer is still in flight: the empty list is not yet a statement. */
  isAssisting: boolean;
  onToggle: (uid: string) => void;
  onToggleDisambiguation: (value: string) => void;
  /**
   * Replace the whole selection, for the counter's two bulk actions. ONE
   * callback taking the new answer rather than a `checkAll`/`uncheckAll` pair:
   * both write the same field, and the set they write is computed HERE — the
   * hosts do not know which practices the axis answer left on screen
   * (`practicesOnAxisSide` runs in this component so every host narrows
   * identically), so they could not name it.
   *
   * Emptying leaves the screen unsubmittable (`canSubmitPracticeProfiles` wants
   * at least one practice), which is the point of holding that invariant in one
   * place: the row can offer the gesture without each host defending the rule.
   */
  onSetPractices: (uids: readonly string[]) => void;
  onPhaseChange: (phase: PracticePickerPhase) => void;
  /**
   * `desktop` is the desktop onboarding's drawing (Figma "D7.pratiques"): rows
   * that carry their answer in their surface rather than in a checkbox, a white
   * context chip, and the screen's lines at body size. The same screen, drawn
   * for a pointer.
   */
  variant: "mobile" | "desktop";
}): React.JSX.Element {
  const { t } = useTranslation();
  const { disambiguation: axis } = offer;
  const rowAffordance =
    variant === "mobile"
      ? ({ kind: "checkbox" } as const)
      : ({ kind: "select" } as const);
  // The rows that act rather than toggle paint the sheet's surface on desktop,
  // as the selectable ones do.
  const actionRowSurface = variant === "desktop" ? "bg-white" : null;
  const lineSize = variant === "mobile" ? "text-ui-note" : "text-sm";

  // DISPLAY order, alphabetical in the reader's language — and display only. The
  // offer arrives in `createdAt` order, which is the order the medical team
  // reviewed the catalogue in, and that array order is what picks WHICH disjoint
  // axis a Specialty with two of them asks about (`disambiguatingAxis`: "the first
  // one the reviewer read"). Sorting the SQL would silently change the question;
  // sorting the rendering changes nothing but where a thumb lands.
  // Sorted on the REAL label now. It used to sort on a hand-written list that
  // recognised 2 of the catalogue's 8 species values and rendered concatenated
  // practice names for the rest — so the alphabetical order was alphabetical in
  // something nobody was reading.
  const sortedAxisOptions =
    axis === null
      ? []
      : [...axis.options].sort((a, b) =>
          getItemTranslation(a).localeCompare(getItemTranslation(b))
        );

  if (phase === "axis") {
    // The phase is resolved from the offer (`resolvePracticePhase`), so this
    // question is only ever up for a Specialty that asks one. The assertion is
    // the inference gap, not a defence: TS cannot carry that from the host down
    // here, and a silent `axis !== null &&` was what let a host hold a phase for
    // a question this screen was not rendering.
    if (axis === null)
      throw new Error(
        "should not happen: unreachable state — the axis question for an offer with no axis"
      );
    return (
      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col",
          variant === "mobile" ? "gap-3.5" : "gap-5"
        )}
      >
        {/* The question, where the surface that owns no title band has to print
            it. On the carousel the header band carries it at the display size
            the design asks for; here it sits under a title the host already
            wrote, so it stays a section heading. */}
        {surface.kind === "settings" && (
          <h3 className="m-0 shrink-0 text-base font-semibold tracking-tight text-ds-black">
            {getItemTranslation({ translations: axis.question })}
          </h3>
        )}
        {specialtyLabel !== null && (
          <ContextChip
            label={specialtyLabel}
            onEdit={onChangeSpecialty}
            variant={variant}
          />
        )}
        {/* Under the chip, and back after a spell away. It was dropped when the
            generated question was expected to end with "Tick all that apply." —
            most of them do not, and a multi-select question that reads as a
            single choice is answered once and left. */}
        <p className={cn("m-0 shrink-0 text-ds-slate-700", lineSize)}>
          {t("Tick all that apply.")}
        </p>

        <div className="scroll-fog-y flex min-h-0 flex-col gap-2 overflow-y-auto">
          {sortedAxisOptions.map((option) => (
            <ListItem
              key={option.value}
              // No count on the side: how many practices sit behind "Adult" is
              // a fact about the catalogue, and what is being asked is who the
              // practitioner is. The number only ever argued for the bigger box.
              affordance={rowAffordance}
              disabled={false}
              selected={
                disambiguation !== null && disambiguation.includes(option.value)
              }
              onClick={() => {
                // A tap COCHES, it does not advance: this question now takes
                // several answers, and a screen that left on the first one could
                // only ever collect one. Leaving is the host's Continue, which
                // `canSubmitPracticeProfiles` holds shut until at least one side
                // is named.
                onToggleDisambiguation(option.value);
              }}
            >
              {/* Whole, on as many lines as it takes: even well named, "NAC,
                  zoo et faune sauvage" in German runs past one line on a narrow
                  phone, and a cut-off label is what this whole lot is about. */}
              <span>{getItemTranslation(option)}</span>
            </ListItem>
          ))}
        </div>
      </div>
    );
  }

  // The axis answer's whole effect on this screen: it NARROWS what is listed to
  // the answered sides plus everything orthogonal to the question, and checks
  // none of it. Surgery goes from 30 rows to 23, Animal Health from 30 to the
  // handful one or two species involve — and a practitioner who answered
  // "adults" is never asked to read past six paediatric practices to find their
  // own.
  //
  // Unanswered (or a Specialty with no axis) lists everything: `practicesOnAxisSide`
  // returns `offer.docs` untouched, which is how the settings editor lets
  // someone walk past the question and still see their whole catalogue.
  const onSide = practicesOnAxisSide({ offer, disambiguation });

  // The search looks at the WHOLE catalogue, not the rows the axis answer left
  // on screen (`usePracticeSearchAssist` runs it). It used to search `onSide`,
  // and that made the narrowing inadvertently final: a practice sitting on a
  // side nobody answered was unfindable, and "no result" was indistinguishable
  // from "not in the catalogue". Harmless while nothing was required; a trap the
  // moment one practice is mandatory, which is this lot.

  const onSideUids = new Set(onSide.map((profile) => profile.uid));

  // The matches the current answer does NOT cover, each with the side that would
  // bring it in. Listed separately and reached by WIDENING rather than by being
  // checked: the practitioner searched for a practice on a side they said they
  // do not work, and the honest repair is the side, not a tick they did not make.
  //
  // A match on no side at all lands in the main list instead (below): no answer
  // to the axis can ever show or hide it, which is precisely what being
  // orthogonal to the axis means.
  const answeredSides = disambiguation === null ? [] : disambiguation;
  const beyondAnswer =
    matching === null
      ? []
      : matching.flatMap((profile) => {
          if (onSideUids.has(profile.uid)) return [];
          const side =
            axis === null
              ? undefined
              : axis.options.find(
                  (option) =>
                    option.profileUids.includes(profile.uid) &&
                    !answeredSides.includes(option.value)
                );
          if (side === undefined) return [];
          return [{ profile, side }];
        });
  const beyondUids = new Set(beyondAnswer.map((row) => row.profile.uid));

  // Alphabetical here too, and for a second reason on top of readability: 30 rows
  // in review order is a list you cannot skim to check whether one is missing.
  // Display only — the ORDER SAVED is this one (it is what `rank` records), which
  // is exactly why the sort has to live where the rows are rendered.
  // Then the ones checked ON OPEN are lifted to the top, alphabetical WITHIN each
  // group: the practitioner's own answer is what this screen asks them to
  // correct, and a catalogue of 30 can push all of it below the fold. Toggling a
  // row does not re-sort — that is the whole reason the order reads a different
  // set from the one the checkmarks read.
  const visible = practiceProfilesInPickerOrder({
    profiles:
      matching === null
        ? [...onSide].sort((a, b) =>
            getItemTranslation(a).localeCompare(getItemTranslation(b))
          )
        : // Already ordered by the host, alphabetical then the model's tail. Not
          // re-sorted: that is what would interleave the tail into rows the
          // practitioner has been reading for two seconds.
          matching.filter((profile) => !beyondUids.has(profile.uid)),
    checkedOnOpen,
  });
  const beyondVisible = [...beyondAnswer].sort((a, b) =>
    getItemTranslation(a.profile).localeCompare(getItemTranslation(b.profile))
  );

  // Every side answered, in the order they were tapped. Not truncated to the
  // first: the second species is exactly the part a practitioner is checking the
  // screen kept. A value the current catalogue no longer carries is DROPPED
  // rather than printed raw — the token is not a word.
  const recapSides =
    axis === null || disambiguation === null
      ? []
      : disambiguation
          .map((value) => {
            const option = axis.options.find(
              (candidate) => candidate.value === value
            );
            return option === undefined ? null : getItemTranslation(option);
          })
          .filter((label) => label !== null);
  const answeredSideLabels =
    recapSides.length === 0 ? null : recapSides.join(", ");

  // What the counter counts: the rows THIS screen shows, the axis answer having
  // already narrowed them, and how many of those are checked. Never the whole
  // answer — a practitioner who changed sides may hold practices this list
  // cannot show, and counting them prints a numerator bigger than the rows.
  // Read off `onSide` rather than `visible`, so a search narrows the list
  // without appearing to drop what is checked.
  const checkedOnSide = onSide.filter((profile) =>
    selected.has(profile.uid)
  ).length;

  // Both bulk actions work on the rows THIS screen lists, which is what makes
  // them agree with the counter above them. Two consequences, both deliberate:
  // a practice held on a side the practitioner no longer names is neither
  // checked nor cleared by a gesture aimed at a list that does not show it, and
  // a SEARCH does not narrow them either — it hides rows for a moment, and
  // "check all" after typing three letters must not mean three practices.
  const listedUids = onSide.map((profile) => profile.uid);
  const kept = [...selected].filter((uid) => !listedUids.includes(uid));

  // The words on offer, once there are any. Trimmed HERE, and handed to
  // `onDeclare`, so the string the row prints is the string it sends.
  const declaration = surface.declaration;
  const declaredQuery = declaration === null ? "" : declaration.query.trim();
  const offersDeclaration = declaration !== null && declaredQuery.length > 0;
  // The saved words, and only where there is a screen left to show them on.
  const settingsDeclaration =
    surface.kind === "settings" ? surface.declaration : null;

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col",
        variant === "mobile" ? "gap-3.5" : "gap-5"
      )}
    >
      {/* The same chip the axis question carries, holding both answers this
          screen rests on. Its Edit reopens the LAST one asked — the axis — and
          the Specialty is then one more Edit away, on the chip up there. That
          is the price of one chip with one action, and it is the right one: two
          controls in a recap row is what made the two screens look like
          different components. */}
      {(specialtyLabel !== null || answeredSideLabels !== null) && (
        <ContextChip
          label={[specialtyLabel, answeredSideLabels]
            .filter((part) => part !== null)
            .join(" · ")}
          onEdit={
            axis === null
              ? onChangeSpecialty
              : () => {
                  onPhaseChange("axis");
                }
          }
          variant={variant}
        />
      )}

      {/* The count is back, and it counts a different thing than the one that
          was dropped. That one ("2 of 19 checked. Uncheck what you don't do.")
          was written when the whole catalogue arrived ticked and pruning was the
          only gesture left; nothing about a catalogue checks a row any more, so
          it was telling most people to uncheck an empty list. This one reads the
          practitioner's own answer back against the rows on screen, and carries
          the one gesture the rows cannot offer: starting over. */}
      <div className="flex shrink-0 items-center justify-between gap-2">
        <p
          className={cn(
            "m-0 text-ds-slate-700",
            variant === "mobile" ? "text-ui-note font-medium" : "text-sm"
          )}
        >
          {t("{{checked}} of {{total}} practices checked", {
            checked: checkedOnSide,
            total: onSide.length,
          })}
        </p>
        {/* Each action renders only while it would DO something — a row that
            does nothing when tapped is worse than an absent one — so the pair
            is only ever both on screen part-way through, and the label under
            the thumb never changes into the opposite action after a tap. */}
        <div className="flex shrink-0 items-center gap-2">
          {surface.kind === "settings" && checkedOnSide < onSide.length && (
            <BulkAction
              label={t("Check all")}
              onClick={() => {
                onSetPractices([...kept, ...listedUids]);
              }}
            />
          )}
          {surface.kind === "settings" &&
            checkedOnSide < onSide.length &&
            checkedOnSide > 0 && (
              <span aria-hidden className="text-ui-note text-ds-slate-400">
                ·
              </span>
            )}
          {checkedOnSide > 0 && (
            <BulkAction
              label={t("Uncheck all")}
              onClick={() => {
                onSetPractices(kept);
              }}
            />
          )}
        </div>
      </div>

      {/* The way out of an empty screen, and it only exists where the screen can
          trap someone: signup refuses an empty answer, so a practitioner the
          agent did not recognise faces nineteen rows with no shortcut left. The
          search field the line points at is not a filter — it runs the typed
          words past a model (`usePracticeSearchAssist`), so "I fit stents"
          reaches a practice no string match would have found.

          Only while nothing is checked. A hint printed over an answered list is
          one more thing to read on the screen this whole change exists to
          lighten. */}
      {surface.kind === "signup" && checkedOnSide === 0 && (
        <p className={cn("m-0 shrink-0 text-ds-slate-600", lineSize)}>
          {/* Two sentences for two screens, not one reworded. In the control arm
              the search is genuinely the only exit and the line has to promise
              a catalogue hit; in the declarative one the field IS the answer,
              and pointing at "the search" would send someone looking for a row
              that is not there. Both keys stay referenced, because
              `pnpm i18n:scan` prunes the one that stops being. */}
          {declaration === null
            ? t("Can't find yours? Describe what you do in the search field.")
            : t("Can't find yours? Specify it in the field below.")}
        </p>
      )}

      {/* Filtering HIDES rows, it never unchecks one, and the count above stays
          the real total — otherwise someone searching for one practice reads it
          as having lost the others. */}
      <div className="scroll-fog-y flex min-h-0 flex-col gap-2 overflow-y-auto">
        {/* The words already saved, above the catalogue rather than inside it:
            they are this practitioner's answer, not a row the search found, and
            the filter below must never hide them. Checked and tappable like
            every other answer here, the tap dropping them — without it the only
            correction available is a replacement, and someone whose practice
            entered the catalogue could never go back to a catalogue answer. */}
        {settingsDeclaration !== null && settingsDeclaration.held !== null && (
          <ListItem
            affordance={rowAffordance}
            selected
            disabled={false}
            onClick={settingsDeclaration.onClear}
          >
            <span className="text-xs font-normal text-ds-slate-500">
              {t("This terminology")}
            </span>
            <span>{settingsDeclaration.held}</span>
          </ListItem>
        )}

        {visible.length === 0 && beyondVisible.length === 0 ? (
          isAssisting ? (
            <div className="flex justify-center pt-4">
              <Loader />
            </div>
          ) : (
            <p className="m-0 pt-4 text-center text-sm text-ds-slate-500">
              {/* An empty result is a different fact on each screen: a search
                  that found nothing where the catalogue is the answer, a
                  catalogue that does not cover this practitioner where their
                  own words are. */}
              {offersDeclaration
                ? t("No practice in the catalogue matches these words.")
                : t("No practice matches your search")}
            </p>
          )
        ) : (
          visible.map((profile) => (
            <ListItem
              key={profile.uid}
              affordance={rowAffordance}
              disabled={false}
              selected={selected.has(profile.uid)}
              onClick={() => {
                onToggle(profile.uid);
              }}
            >
              {/* Never clamped: the catalogue's names run long ("Minimally
                  invasive thoracic surgery (VATS and RATS)"), and a name cut at
                  two lines hid the end that tells two practices apart. */}
              <span>{getItemTranslation(profile)}</span>
            </ListItem>
          ))
        )}

        {/* After the catalogue's matches, before the other-side rows. A
            reviewed practice carries a MeSH Boolean somebody checked, so it
            outranks an automatic resolution — but a row from a side the
            practitioner did not name costs a tap that only widens the list,
            and their own words are the shorter answer.

            A submission, not an addition — the tap saves the slide and leaves,
            so there is no screen afterwards on which to re-read what was sent.
            That is why the row prints the words back: what goes must be legible
            in the control that sends it. Unquoted, because every locale
            punctuates quotation differently and a key made of punctuation does
            not survive the i18n scan. */}
        {offersDeclaration && declaration !== null && (
          <ListItem
            affordance={{ kind: "action" }}
            selected={false}
            disabled={false}
            onClick={() => {
              declaration.onDeclare(declaredQuery);
            }}
            // Accented, because this row is the other way OUT of the question
            // rather than another catalogue entry. The border and the label
            // carry it, never the moss surface: on this list that surface means
            // "checked", and a row whose tap submits must not arrive looking
            // like an answer already given.
            className={cn(
              variant === "mobile"
                ? "border-ds-border-selected"
                : "border-ds-mint-700 text-sm",
              actionRowSurface
            )}
          >
            <span className="truncate text-ds-primary">
              {t("Keep this terminology")}
            </span>
            {/* Whole and not truncated: a full-width sentence is what this
                field produces, and an ellipsis hides the part the row exists to
                show. */}
            <span className="text-xs font-normal text-ds-slate-500">
              {declaredQuery}
            </span>
          </ListItem>
        )}

        {/* What the search found on a side the practitioner did not name. Under a
            heading rather than mixed in, because these rows do something else: a
            tap adds the SIDE and checks nothing, so the list widens and the
            practice is then a normal row one tap away. */}
        {beyondVisible.length > 0 && (
          <>
            <p className="m-0 shrink-0 pt-2 text-xs font-medium text-ds-slate-600">
              {t("Elsewhere in your specialty")}
            </p>
            {beyondVisible.map((row) => (
              <ListItem
                key={row.profile.uid}
                affordance={{ kind: "action" }}
                selected={false}
                disabled={false}
                onClick={() => {
                  onToggleDisambiguation(row.side.value);
                }}
                className={cn("text-sm", actionRowSurface)}
              >
                <span className="truncate">
                  {getItemTranslation(row.profile)}
                </span>
                <span className="truncate text-xs font-normal text-ds-slate-500">
                  {t("Add {{group}} to see this practice", {
                    group: getItemTranslation(row.side),
                  })}
                </span>
              </ListItem>
            ))}
          </>
        )}
      </div>

      {/* Under the row and outside the scroller, where the practitioner is
          being asked to trust a screen that just told them it has nothing. */}
      {offersDeclaration &&
        !isAssisting &&
        visible.length === 0 &&
        beyondVisible.length === 0 && (
          <p className="m-0 shrink-0 text-ui-note text-ds-slate-600">
            {t("We add the practices practitioners tell us about.")}
          </p>
        )}
    </div>
  );
}

/**
 * One of the counter's two bulk actions. Inlined as a component only because
 * the row holds two of them and a copied class string is how the pair drifts
 * into looking like two different kinds of control.
 */
function BulkAction({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="shrink-0 cursor-pointer border-none bg-transparent p-0 font-dm-sans text-ui-note font-medium text-ds-mint-700 underline"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * What has been answered ABOVE the question on screen, and the way back to it
 * (Figma node 6681:3600). One element for both of this screen's questions: the
 * axis one names the Specialty, the list names the Specialty and the sides, and
 * the two used to be two chips that had drifted into two different shapes.
 *
 * ONE action, always the way back to the LAST question answered. From the list
 * that is the axis, so the Specialty is one Edit further — up on the chip the
 * axis question carries. Two links in a recap row is what the drift was.
 *
 * A null `onEdit` renders the label alone: settings edits the Specialty on
 * another screen, and a single-specialty profession was never asked.
 */
function ContextChip({
  label,
  onEdit,
  variant,
}: {
  label: string;
  onEdit: (() => void) | null;
  /** `desktop` is a white field-shaped row (Figma "specialty-chip-row"). */
  variant: "mobile" | "desktop";
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "flex shrink-0 items-center gap-2 px-4",
        variant === "mobile"
          ? "rounded-2xl bg-ds-slate-50 py-3"
          : "h-12 rounded-xl border border-ds-black/6 bg-ds-white"
      )}
    >
      <span className="min-w-0 flex-1 truncate text-sm font-medium tracking-tight text-ds-primary">
        {label}
      </span>
      {onEdit !== null && (
        <button
          type="button"
          className={cn(
            "shrink-0 cursor-pointer border-none bg-transparent p-0 font-dm-sans text-ui-note text-ds-mint-700 underline",
            variant === "desktop" && "font-medium"
          )}
          onClick={onEdit}
        >
          {t("Edit")}
        </button>
      )}
    </div>
  );
}

// The axis question and its option labels used to be TWO HAND-WRITTEN LISTS
// here, because the API could not name a facet value and this screen had to
// render something. They are gone: the names come from `practice_axes` through
// the offer, the same `getItemTranslation` call the practices themselves use.
//
// Kept as a note rather than deleted silently, because the failure they caused is
// the reason for the table. The lists had drifted — 2 of the catalogue's 8
// species values were still recognised, 7 entries named values that no longer
// existed — and the fallback ("the names of the practices behind this option,
// concatenated") rendered that drift as plausible French. The species question
// listed truncated specialty names for months and nobody saw it, because a
// fallback always returns something.
